// @flow

import React, { useEffect, useState, useRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { push } from "connected-react-router";
import classNames from "classnames";
import { TooltipDropdown, View, Modal, AITeamChat, Text } from "@components";
import { route } from "@routes/helper";
import type { State } from "@store/reducers";
import {
  withPermissions,
  type PropsFromPermissions,
  PermissionEnum,
} from "@modules/account/components";
import { ContextMenu, MenuItem, Icons, Button } from "@components";
import * as accountSelectors from "@modules/account/selectors";
import actionCreators from "@modules/account/actionCreator";
import reportingActionCreators from "@modules/reporting/actionCreator";
import { getCurrentPath } from "@selectors";
import "./header.style.scss";
import { getSystemMessagesReleaseNote } from "@modules/shared/selectors";
import * as reportingSelectors from "@modules/reporting/selectors";
import type { ProfileClient } from "@modules/account/types";
import { TextField } from "@components";
import employeesActionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import { getClientContext } from "@modules/context";
import type { Client } from "@modules/context/types";
import * as routes from "@routes/constants";
import KeycloakAdapter from "@src/keycloak";
import { USER_TYPES } from "@modules/account/constants";
import type { ReleaseNote } from "@modules/shared/selectors";
import config from "@config";
import http from "@http";
type OwnProps = $Exact<{
  currentPath: string,
  navigate: (event: SyntheticEvent<HTMLElement>) => void,
  toggleSidebar: () => void,
  isCollapsed: boolean,
  permissions: { [permission: string]: boolean } | null,
}>;

type PropsFromState = $Exact<{
  userFirstName: string,
  isAuthenticated: boolean,
  currentPath: string,
  releaseNote: ReleaseNote | null,
  dashboardToday: Array<Object>,
  userClient: ProfileClient,
  clientId: number | null,
  client: Client | null,
  hasContextSwitch: boolean,
  hasReportingContextSwitch: boolean,
  requestReportingDashboardOverview: boolean,
  userType?: string,
  allowEmployeeAudit: boolean,
}>;

type PropsFromDispatch = $Exact<{
  redirect: Function,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromPermissions,
}>;

type StateProps = $Exact<{
  last_name: string,
}>;

function AppHeader(props: Props) {
  const API = config.AITeamAPI;
  const getHealthTimer = useRef(null);
  const [lastName, setLastName] = useState("");
  const [aiTeamModalToggled, setAiTeamModalToggled] = useState(false);
  const [health, setHealth] = useState("not-ok");

  const allowedToUseAITeam =
    (props.permissions &&
      (props.permissions[PermissionEnum.PRIVILEGE_AI_TEAM_BOMAN] ||
        props.permissions[PermissionEnum.PRIVILEGE_AI_TEAM_CLAUDIA] ||
        props.permissions[PermissionEnum.PRIVILEGE_AI_TEAM_MORGAN] ||
        props.permissions[PermissionEnum.PRIVILEGE_AI_TEAM_SHANI])) ||
    false;

  useEffect(() => {
    function isWindowFocused() {
      return document.visibilityState === "visible" && document.hasFocus();
    }

    function stopHealthTimer() {
      if (getHealthTimer.current) {
        clearInterval(getHealthTimer.current);
        getHealthTimer.current = null;
      }
    }

    function startHealthTimer() {
      if (!isWindowFocused() || getHealthTimer.current) return;

      getHealth();

      getHealthTimer.current = setInterval(function() {
        if (isWindowFocused()) {
          getHealth();
        } else {
          stopHealthTimer();
        }
      }, 300000);
    }

    function handleWindowStateChange() {
      if (isWindowFocused()) {
        startHealthTimer();
      } else {
        stopHealthTimer();
      }
    }

    startHealthTimer();
    window.addEventListener("focus", handleWindowStateChange);
    window.addEventListener("blur", handleWindowStateChange);
    document.addEventListener("visibilitychange", handleWindowStateChange);

    return function() {
      stopHealthTimer();
      window.removeEventListener("focus", handleWindowStateChange);
      window.removeEventListener("blur", handleWindowStateChange);
      document.removeEventListener("visibilitychange", handleWindowStateChange);
    };
  }, []);

  function getHealth() {
    http.get(`${API}api/ai/health`).then(({ data }) => {
      setHealth(data.status);
    });
  }
  function redirect(path: string, filters?: Object) {
    return function() {
      props.redirect(path, filters);
    };
  }

  function handleSubmit() {
    if (props.client) {
      typeof adminOrReportPage === "string" &&
        redirect(
          route("REPORTING_EMPLOYEES_SEARCH_PATH", {
            clientId: (props.client || {}).id,
          }),
          {
            lastName: lastName,
            isActive: true,
          },
        )();
    }
  }

  function hasAccessToTasks() {
    // TODO - need to provide a permission from the BE, that is linked to the user types below
    const res =
      [USER_TYPES.ADMIN, USER_TYPES.SERVICE_MANAGER, USER_TYPES.CLIENT_SUPPLIER].indexOf(
        props.userType,
      ) === -1;
    return res;
  }

  function handleNewsClick() {
    window.open("https://fast.wistia.com/embed/channel/uj1w20q8xu", "_blank");
  }
  function handleHelpClick() {
    window.open("https://fast.wistia.com/embed/channel/ww9dp1z2r7", "_blank");
  }

  const adminOrReportPage =
    (props.currentPath.match(/(administration|reporting)/g) || [])[0] || false;

  function showClientSwitch() {
    const { hasContextSwitch, client, currentPath, hasReportingContextSwitch } = props;
    const adminPage = currentPath.startsWith(routes.ADMINISTRATION_PATH);
    const reportingPage = currentPath.startsWith(routes.REPORTING_PATH);

    return hasContextSwitch && client && (adminPage || reportingPage);
  }

  function onChange(e) {
    setLastName(e.target.value);
  }
  function onKeyUp(e) {
    if (e.keyCode === 13) handleSubmit();
  }

  return (
    <View
      name="AppHeader"
      position="sticky"
      top="0px"
      padding={[2, 6, 2, 6]}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      separator="bottom"
      minHeight="67px"
      zIndex="1"
    >
      <View alignItems="center" direction="row">
        <Button
          onClick={props.toggleSidebar}
          color="secondary"
          materialIcon="menu"
          className="pl-0"
        />
        {showClientSwitch() && (
          <View>
            <div
              className="text-color-berry-d1 font-size-16 ellipsis cursor-pointer"
              onClick={redirect(route("ADMINISTRATION_CLIENT_PATH"))}
            >
              {(props.client || {}).name || ""}
            </div>
          </View>
        )}
      </View>
      {props.isAuthenticated === true && (
        <View direction="row" alignItems="center">
          {adminOrReportPage && (
            <View>
              <TextField
                disabled={props.client === null}
                name="lastName"
                width="230px"
                materialIcon={
                  <Button
                    disabled={props.client === null}
                    color="secondary"
                    className="position-absolute right-0"
                    materialIcon="search"
                    onClick={handleSubmit}
                  />
                }
                type="text"
                className="font-size-14"
                onChange={onChange}
                onKeyUp={onKeyUp}
                placeholder={props.t("SEARCH_EMPLOYEE_SURNAME")}
              />
            </View>
          )}

          {props.userType !== USER_TYPES.EMPLOYEE && (
            <View gap={2} direction="row" alignItems="center"></View>
          )}
        </View>
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  userFirstName: accountSelectors.getFirstName(state),
  isAuthenticated: accountSelectors.isAuthenticated(state),
  currentPath: getCurrentPath(state),
  releaseNote: getSystemMessagesReleaseNote(state),
  dashboardToday: reportingSelectors.getLoggedUserDashboardToday(state),
  userClient: accountSelectors.getUserClient(state),
  clientId: accountSelectors.getUserClientId(state),
  client: getClientContext(state),
  hasContextSwitch: accountSelectors.hasClientContextSwitch(state),
  hasReportingContextSwitch: accountSelectors.hasReportingClientContextSwitch(state),
  requestReportingDashboardOverview: accountSelectors.accessReportingDashboardOverviewData(state),
  userType: accountSelectors.selectUserType(state),
  allowEmployeeAudit: accountSelectors.getUserAllowEmployeeAudit(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  redirect: push,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({}),
)(AppHeader);
